/**
 * Create wrapper around callback with delayed execution. When "debounced" function is called several times - callback will be executed only once after delay.
 *
 * @param {Function} callback Callback.
 * @param {number} ms Delay.
 * @param {{ leading: ?Boolean, trailing: ?Boolean }} options Settings.
 */
export function debounce(callback, ms, options = {}) {
    let timeoutId = null;
    let lastArgs = null;
    let lastThis;
    let result;
    const { leading: isLeading = false, trailing: isTrailing = true } = options;
    function invokeFunc() {
        result = callback.apply(lastThis, lastArgs);
        lastArgs = null;
        lastThis = null;
        return result;
    }
    function leadingEdge() {
        if (isLeading) {
            invokeFunc();
        }
        timeoutId = setTimeout(trailingEdge, ms);
    }
    function trailingEdge() {
        timeoutId = null;
        if (isTrailing && lastArgs) {
            invokeFunc();
        }
    }
    function debounced(...args) {
        lastArgs = args;
        lastThis = this;
        if (timeoutId === null) {
            leadingEdge();
        }
        else {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(trailingEdge, ms);
        }
        return result;
    }
    debounced.reset = () => {
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
        }
        timeoutId = null;
        lastArgs = null;
        lastThis = null;
    };
    return debounced;
}
/**
 * Promise version of `debounce`. Allows to wait until `debounce` will be executed.
 *
 * @param {Function} callback Callback.
 * @param {number} ms Delay.
 * @param {{ leading: ?Boolean, trailing: ?Boolean }} options Settings.
 */
export function asyncDebounce(callback, ms, options = {}) {
    const debounced = debounce((resolve, reject, args) => {
        callback(...args).then(resolve).catch(reject);
    }, ms, options);
    return (...args) => new Promise((resolve, reject) => {
        debounced(resolve, reject, args);
    });
}
